"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "@/lib/motion";
import { useFormik } from "formik";
import { ArrowRight, CheckCircle2 } from "@/components/icons";
import { Badge, AuthorAvatar, FormError, brandActionClasses } from "@/components/ui";
import { Input } from "@/components/ui/input";
import { fieldTouchedError } from "@/lib/form-helpers";
import { emailOnlySchema } from "@/lib/form-schemas";
import type { ArchivePost } from "./types";
import { cn } from "@/lib/utils";

const CURVE_PATHS = [
  "M 0 120 C 60 120 80 60 120 50 C 160 40 180 100 220 80 C 260 60 300 30 360 20 C 400 14 440 40 500 30",
  "M 0 100 C 40 100 60 140 100 130 C 140 120 160 50 220 40 C 280 30 320 90 380 70 C 420 55 460 80 500 60",
  "M 0 130 C 50 130 80 80 130 70 C 180 60 200 110 260 90 C 310 75 360 40 420 35 C 460 30 480 60 500 55",
  "M 0 80 C 60 80 100 120 160 110 C 200 104 220 60 280 50 C 330 42 370 80 420 65 C 460 54 480 90 500 80",
];

export function CategoryBadge({
  category,
  color,
  icon,
}: {
  category: string;
  color: string;
  icon?: ReactNode;
}) {
  return (
    <Badge
      variant="secondary"
      className="flex items-center gap-1 rounded-full border-0 px-2 py-0.5 text-xs font-medium"
      style={{ color, backgroundColor: color + "18" }}
    >
      {icon}
      {category}
    </Badge>
  );
}

export function PostVisual({
  color,
  seed,
  ns = "arc",
}: {
  color: string;
  seed: number;
  ns?: string;
}) {
  const path = CURVE_PATHS[seed % CURVE_PATHS.length];
  const patternId = `${ns}-${seed}`;
  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ background: "#0d0d0f" }}
      aria-hidden="true"
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 500 160"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
      >
        <defs>
          <pattern id={patternId} width="24" height="24" patternUnits="userSpaceOnUse">
            <path
              d="M 24 0 L 0 0 0 24"
              fill="none"
              stroke="white"
              strokeWidth="0.3"
              strokeOpacity="0.06"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
        <path
          d={path}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          opacity="0.80"
        />
        <path
          d={path}
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          fill="none"
          opacity="0.06"
        />
      </svg>
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 60% 70% at 50% 50%, ${color}0d, transparent 70%)`,
        }}
      />
    </div>
  );
}

export function PostListItem({
  post,
  index,
  color,
  readMoreLabel = "Read post",
  ns,
}: {
  post: ArchivePost;
  index: number;
  color: string;
  readMoreLabel?: string;
  ns?: string;
}) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="group border-border hover:bg-foreground/1.5 relative cursor-pointer rounded-xl border-b py-8 transition-colors last:border-b-0 sm:-mx-4 sm:px-4"
    >
      <Link
        href={post.href}
        aria-label={post.title}
        className="relative z-10 mb-5 block overflow-hidden rounded-xl"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="relative w-full" style={{ paddingBottom: "52%" }}>
          {post.image ? (
            <Image
              src={post.image}
              alt={post.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-contain"
              style={{ filter: "hue-rotate(-70deg) saturate(0.5) brightness(1.1)" }}
            />
          ) : (
            <div className="absolute inset-0">
              <PostVisual color={color} seed={post.id} ns={ns} />
            </div>
          )}
        </div>
      </Link>
      <h3 className="text-foreground mb-2 text-lg leading-snug font-semibold">
        <Link href={post.href} className="after:absolute after:inset-0">
          {post.title}
        </Link>
      </h3>
      <div className="mb-4 flex items-center gap-2">
        {post.authorImage ? (
          <Image
            src={post.authorImage}
            alt={post.author}
            width={28}
            height={28}
            className="h-7 w-7 shrink-0 rounded-full object-cover"
          />
        ) : (
          <AuthorAvatar initial={post.authorInitial} color={color} className="h-7 w-7 text-xs" />
        )}
        <span className="text-muted-foreground text-sm">
          {post.authorUid ? (
            <Link
              href={`/author/${post.authorUid}`}
              className="text-foreground relative z-10 font-medium underline-offset-2 hover:underline"
            >
              {post.author}
            </Link>
          ) : (
            <span className="text-foreground font-medium">{post.author}</span>
          )}
          {" on "}
          {post.date}
        </span>
      </div>
      <p className="text-muted-foreground mb-4 line-clamp-2 text-sm leading-relaxed">
        {post.excerpt}
      </p>
      <Link
        href={post.href}
        className="group/link relative z-10 inline-flex items-center gap-1.5 text-sm font-semibold transition-all"
        style={{ color }}
        aria-label={`${readMoreLabel}: ${post.title}`}
      >
        {readMoreLabel}
        <ArrowRight size={13} className="transition-transform group-hover/link:translate-x-1" />
      </Link>
    </motion.article>
  );
}

export function HeroFeaturedCard({
  post,
  color,
  readMoreLabel = "Read post",
  visual,
  categoryBadge,
}: {
  post: ArchivePost;
  color: string;
  readMoreLabel?: string;
  visual: ReactNode;
  categoryBadge: ReactNode;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="bg-card group border-foreground/4 hover:border-foreground/10 relative w-full cursor-pointer overflow-hidden rounded-2xl border transition-colors"
      style={{ minHeight: 360 }}
      aria-label={`Featured: ${post.title}`}
    >
      <div className="grid sm:grid-cols-[11fr_9fr]" style={{ minHeight: 360 }}>
        <Link
          href={post.href}
          className="relative block sm:h-full sm:min-h-0"
          tabIndex={-1}
          aria-hidden="true"
        >
          {post.image ? (
            <>
              <Image
                src={post.image}
                alt={post.title}
                width={1600}
                height={900}
                sizes="(max-width: 640px) 100vw, 55vw"
                className="block h-auto w-full sm:hidden"
                style={{ filter: "hue-rotate(-70deg) saturate(0.5) brightness(1.1)" }}
                priority
              />
              <Image
                src={post.image}
                alt={post.title}
                fill
                sizes="55vw"
                className="hidden object-cover sm:block"
                style={{ filter: "hue-rotate(-70deg) saturate(0.5) brightness(1.1)" }}
                priority
              />
            </>
          ) : (
            visual
          )}
        </Link>
        <div className="border-foreground/6 relative flex flex-col border-t p-7 sm:border-t-0 sm:border-l sm:p-9">
          <div className="mb-5">{categoryBadge}</div>
          <h2 className="text-foreground mb-4 text-xl leading-snug font-bold sm:text-2xl">
            <Link href={post.href} className="after:absolute after:inset-0">
              {post.title}
            </Link>
          </h2>
          <div className="mb-4 flex items-center gap-2">
            {post.authorImage ? (
              <Image
                src={post.authorImage}
                alt={post.author}
                width={28}
                height={28}
                className="h-7 w-7 shrink-0 rounded-full object-cover"
              />
            ) : (
              <AuthorAvatar
                initial={post.authorInitial}
                color={color}
                className="h-7 w-7 text-xs"
              />
            )}
            <span className="text-muted-foreground text-sm">
              {post.authorUid ? (
                <Link
                  href={`/author/${post.authorUid}`}
                  className="text-foreground relative z-10 font-medium underline-offset-2 hover:underline"
                >
                  {post.author}
                </Link>
              ) : (
                <span className="text-foreground font-medium">{post.author}</span>
              )}
              {" on "}
              {post.date}
            </span>
          </div>
          <p className="text-muted-foreground line-clamp-3 flex-1 text-sm leading-relaxed">
            {post.excerpt}
          </p>
          <div className="mt-6">
            <Link
              href={post.href}
              className="group/link relative z-10 inline-flex items-center gap-1.5 text-sm font-semibold transition-all"
              style={{ color }}
              aria-label={`${readMoreLabel}: ${post.title}`}
            >
              {readMoreLabel}
              <ArrowRight
                size={14}
                className="transition-transform group-hover/link:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export function ArchiveNewsletterForm({ ariaLabel = "Newsletter signup" }: { ariaLabel?: string }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: emailOnlySchema,
    onSubmit: async (values, { resetForm }) => {
      setSubmitError(null);
      try {
        const body = new FormData();
        body.append("email", values.email);
        const res = await fetch("/api/subscribe", { method: "POST", body });
        if (!res.ok) throw new Error("failed");
        setSubmitted(true);
        resetForm();
      } catch {
        setSubmitError("Something went wrong. Please try again.");
      }
    },
  });

  if (submitted) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-2.5"
      >
        <CheckCircle2 size={16} className="shrink-0 text-green-500" />
        <span className="text-sm font-medium text-green-600 dark:text-green-400">
          You&apos;re subscribed — thank you!
        </span>
      </div>
    );
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex w-full flex-col gap-1 sm:w-auto"
      aria-label={ariaLabel}
      noValidate
    >
      <div className="flex w-full gap-2 sm:w-auto">
        <div className="flex min-w-0 flex-1 flex-col gap-1 sm:w-64">
          <Input
            type="email"
            name="email"
            placeholder="my@email.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="bg-card h-10 w-full"
            aria-label="Email address"
            aria-invalid={!!(formik.touched.email && formik.errors.email)}
          />
          {fieldTouchedError(formik, "email") && (
            <FormError>{fieldTouchedError(formik, "email")}</FormError>
          )}
          {submitError && <FormError>{submitError}</FormError>}
        </div>
        <button
          type="submit"
          disabled={formik.isSubmitting}
          className={cn(
            brandActionClasses,
            "h-10 shrink-0 cursor-pointer self-start rounded-xl px-5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          )}
        >
          {formik.isSubmitting ? "Subscribing…" : "Subscribe"}
        </button>
      </div>
    </form>
  );
}
